import { RPCService } from "../services/rpc.js";
import React, { useEffect, useMemo, useRef } from "react";
import { jsx } from "react/jsx-runtime";
import { RPCClient } from "@therms/rpc-client";
//#region src/contexts/RPCContext.tsx
var RPCContext = React.createContext(void 0);
window.rpcClientsByHash = {};
function RPCContextProvider({ children, options, rpcClient, testEnv, useRPCDefaultOpts }) {
	const wsSubscriptions = useRef({ subscribedEvents: /* @__PURE__ */ new Set() });
	if (!rpcClient && !options?.hosts?.http) throw new Error("<RPCContextProvider /> requires \"options.hosts.http\" prop");
	const [_rpcClient, rpcService, wsUtils] = useMemo(() => {
		const rpcClientHashKey = `${options?.hosts?.http}${options?.hosts?.websocket}`;
		let client;
		/**
		* During development, we have module hot reloading and multiple clients
		* get created - this prevents that from happening.
		*
		* NOTE: It could turn into
		* an issue in a complex client environment where there are multiple
		* RPCClients intended to be created with the same host values.
		*/
		if (rpcClient) client = rpcClient;
		else if (window.rpcClientsByHash[rpcClientHashKey]) client = window.rpcClientsByHash[rpcClientHashKey];
		else {
			const newRPCClient = new RPCClient({
				...options,
				transportOptions: {
					...options.transportOptions,
					websocket: {
						autoConnect: false,
						...options.transportOptions?.websocket
					}
				}
			});
			window.rpcClientsByHash[rpcClientHashKey] = newRPCClient;
			client = newRPCClient;
		}
		return [
			client,
			RPCService(client),
			{
				wsGetSubscribedEvents: () => Array.from(wsSubscriptions.current.subscribedEvents),
				wsSubscribe: (event) => {
					wsSubscriptions.current.subscribedEvents.add(event);
					client?.sendClientMessageToServer(`subscribe::${event}`);
				},
				wsUnsubscribe: (event) => {
					wsSubscriptions.current.subscribedEvents.delete(event);
					client?.sendClientMessageToServer(`unsubscribe::${event}`);
				},
				wsUnsubscribeAll: () => {
					wsSubscriptions.current.subscribedEvents.clear();
					client?.sendClientMessageToServer(`unsubscribe::all`);
				}
			}
		];
	}, [rpcClient]);
	useEffect(() => {
		return rpcClient?.registerWebSocketConnectionStatusChangeListener((connected) => {
			if (connected) wsUtils.wsGetSubscribedEvents().forEach((event) => {
				rpcClient?.sendClientMessageToServer(`subscribe::${event}`);
			});
		});
	}, [rpcClient]);
	useEffect(() => {
		if (window.rpcClient) console.warn("RPCClient already exists on window object - overwriting...");
		window.rpcClient = _rpcClient;
		window.rpcService = rpcService;
	}, [_rpcClient, rpcService]);
	const ctxValue = useMemo(() => {
		return {
			options,
			rpcClient: _rpcClient,
			rpcService,
			testEnv: !!testEnv,
			useRPCDefaultOpts,
			...wsUtils
		};
	}, [
		_rpcClient,
		rpcService,
		testEnv,
		useRPCDefaultOpts,
		wsUtils
	]);
	return /* @__PURE__ */ jsx(RPCContext.Provider, {
		value: ctxValue,
		children
	});
}
function useRPCContext() {
	const context = React.useContext(RPCContext);
	if (context === void 0) throw new Error("useRPCContext() must be a descendant of <RPCContextProvider />");
	return context;
}
//#endregion
export { RPCContextProvider, useRPCContext };

//# sourceMappingURL=RPCContext.js.map